<template>
  <div>
    <el-button
      size="small"
      type="primary"
      class="action"
      :disabled="!changes"
      @click.prevent="save"
    >
      Save and Restart
    </el-button>

    <el-divider content-position="left">
      <h3>Web Settings</h3>
    </el-divider>

    <SettingCard v-model="config.hostname" :options="settingDomains">
      <template slot="title">Primary domain</template>
      Manage the prefered domain that will be served to your web visitors.
    </SettingCard>

    <SettingCard
      v-if="maximumNetworks > 0 && instance.engine === '4'"
      :enabled="publicIp"
      :enable="enableDedicatedIp"
      :available="isAvailableNetwork"
      :unavailable-text="unavailableNetworks"
    >
      <template slot="title">Dedicated IP</template>
      Attach a dedicated IP to your site.
    </SettingCard>

    <SettingCard v-model="config.proxy_scheme" on="https" off="http">
      <template slot="title">HTTPS</template>
      Encrypt communication to and from your website using SSL.
    </SettingCard>

    <SettingCard v-model="config.proxy_https_redirect" on="1" off="0">
      <template slot="title">Always use HTTPS</template>
      Redirect all <abbr>http</abbr> requests to <abbr>https</abbr>.
    </SettingCard>

    <SettingCard v-model="config.proxy_https_rewrite" on="1" off="0">
      <template slot="title">Automatic rewrites</template>
      Change <abbr>http</abbr> to <abbr>https</abbr> for all resources or links on your website.
    </SettingCard>

    <SettingCard v-model="config.proxy_google_brotli" on="1" off="0">
      <template slot="title">Brotli</template>
      Speed up page load times for your website HTTPS traffic by applying Brotli compression.
    </SettingCard>

    <SettingCard v-model="config.proxy_restricted" on="1" off="0">
      <template slot="title">Restricted access</template>
      Protect internal resources by authenticating website using runstack user credentials.
    </SettingCard>

    <SettingCard v-model="config.proxy_no_index" on="1" off="0">
      <template slot="title">Search engine visibility</template>
      Discourage search engines from indexing this site.
    </SettingCard>

    <SettingCard v-model="config.proxy_google_pagespeed" on="1" off="0">
      <template slot="title">PageSpeed</template>
      Help making website faster by rewriting pages to reduce latency and bandwidth.
    </SettingCard>

    <SettingCard v-model="config.proxy_robots_mitigation" on="1" off="0">
      <template slot="title">Robot mitigation</template>
      Challenge cookies using HTML meta refresh redirect.
    </SettingCard>

    <SettingCard
      v-model="config.proxy_google_analytics"
      placeholder="UA-XXXXXXXX-X"
    >
      <template slot="title">Google Analytics</template>
      Add the GA Tracking Code to your site. Log in to your Google account to view your stats.
    </SettingCard>

    <SettingCard
      v-model="config.proxy_robots_txt"
      type="textarea"
      :autosize="{ minRows: 4 }"
      :maxlength="6000"
    >
      <template slot="title">Robots.txt</template>
    </SettingCard>
  </div>
</template>

<script>
import SettingCard from './SettingCard'

export default {
  props: {
    instance: Object,
    application: Object,
    domains: Array
  },
  components: {
    SettingCard
  },
  data () {
    return {
      loading: false,
      changes: false,
      config: { ...this.application.config || {} }
    }
  },
  computed: {
    settingDomains () {
      return this.domains.map((domain) => domain.name)
    },
    publicIp () {
      if (this.application.publicIp === 'pending') {
        return '<em><i class="el-icon-loading"></i> ' + 'being validated' + '</em>'
      }
      const network = (this.instance.networks || []).find((n) => n.publicIp === this.application.publicIp)
      return network ? network.publicIp : false
    },
    isAvailableNetwork () {
      return (this.instance.networks || []).filter((n) => n.publicIp).length < this.maximumNetworks
    },
    maximumNetworks () {
      return this.planNetworks(this.instance.planId) || 0
    },
    unavailableNetworks () {
      return 'reached maximum dedicated IP' + ` (${this.maximumNetworks})`
    }
  },
  methods: {
    enableDedicatedIp () {
      return new Promise((resolve, reject) => {
        this.$confirm('This will create a dedicated IP for this application. Continue?', 'Warning', {
          confirmButtonText: 'Create dedicated IP',
          cancelButtonText: 'Cancel',
          type: 'warning'
        }).then(() => {
          this.$store.dispatch('dedicatedIpApplication', this.application).then(resolve).catch(reject)
        }).catch(resolve)
      })
    },
    save () {
      this.$emit('save', { config: this.config })
      this.changes = false
    }
  },
  watch: {
    config: {
      handler () {
        this.changes = true
      },
      deep: true
    }
  }
}
</script>
